/**
 * Pure helpers for forwarding legacy (pre-tenancy) `/dashboard/...` URLs into the
 * tenant-prefixed structure. Kept free of React/router imports so it can be unit
 * tested directly.
 *
 * Two scopes:
 *  - ORG-scoped legacy paths (`/dashboard/settings/...`, `/dashboard/clients`)
 *    forward to `/dashboard/o/:org/...` (no client segment). The brand detail
 *    still carries its own `/$clientId` further down the path.
 *  - everything else is client-scoped and forwards to `/dashboard/o/:org/c/:client/...`.
 *
 * The query + hash are preserved verbatim, which is what keeps OAuth returns
 * working: the backend still redirects to the old
 * `/dashboard/settings/client/brand/:clientId/integrations/:provider?success=true`
 * URL, which 404s into this forwarder and lands on the org-prefixed route with
 * `?success`/`?error`/`?step` intact for the integration page to read.
 *
 * `/chat/:templateId` is forwarded too. It was never a route -- MCP tools and
 * the coding agents minted it for months and handed it to customers -- so the
 * links are already in transcripts, seed folders, and inboxes we cannot go
 * back and edit. The emitters now build the tenant path directly; this keeps
 * the ones already out there landing on the email instead of a 404.
 */

export const DASHBOARD_ROOT = '/dashboard';

// Legacy `/dashboard/<seg>` segments that are org-scoped, not client-scoped.
const ORG_SCOPED_SEGMENTS = ['settings', 'clients'];

// `/chat/<templateId>`, and nothing deeper: the id is one segment, so a longer
// path is some other unknown URL and belongs on the not-found page.
const LEGACY_CHAT_PATH = /^\/chat\/([^/]+)\/?$/;

/**
 * True for a legacy path that still needs forwarding into the tenant prefix:
 * an old `/dashboard/...` link, or a minted `/chat/:templateId` one. A path
 * already under `/dashboard/o/` is considered migrated (returns false) so the
 * forwarder never loops.
 */
export function isLegacyDashboardPath(pathname: string): boolean {
  if (LEGACY_CHAT_PATH.test(pathname)) return true;
  if (pathname !== DASHBOARD_ROOT && !pathname.startsWith(`${DASHBOARD_ROOT}/`)) {
    return false;
  }
  return !pathname.startsWith(`${DASHBOARD_ROOT}/o/`);
}

function isOrgScopedRest(rest: string): boolean {
  // rest is '' or starts with '/'. Match the first segment.
  const seg = rest.replace(/^\//, '').split('/')[0];
  return ORG_SCOPED_SEGMENTS.includes(seg);
}

/**
 * Build the tenant-prefixed target for a legacy dashboard path.
 * @param pathname e.g. '/dashboard/settings/client/brand/c1/integrations/shopify'
 *   or a minted '/chat/:templateId'
 * @param queryHash the `location.search + location.hash` to preserve (may be '')
 */
export function buildTenantRedirectTarget(
  pathname: string,
  queryHash: string,
  orgId: string,
  clientId: string,
): string {
  const chat = LEGACY_CHAT_PATH.exec(pathname);
  if (chat) {
    return `${DASHBOARD_ROOT}/o/${orgId}/c/${clientId}/emails/${chat[1]}/chat${queryHash}`;
  }

  const rest = pathname.slice(DASHBOARD_ROOT.length); // '' | '/templates' | '/settings/...'
  const base = isOrgScopedRest(rest)
    ? `${DASHBOARD_ROOT}/o/${orgId}`
    : `${DASHBOARD_ROOT}/o/${orgId}/c/${clientId}`;
  return `${base}${rest}${queryHash}`;
}
